import { useTranslation } from 'react-i18next';
import { MarketAccessory, MarketRow } from '@market/react';
import { CartSummaryPlan } from 'typescript/types/api/solidshop';
import { getLocaleString } from 'typescript/utilities/locales';
import { toCurrency } from 'typescript/utilities/currency';

import {
  getSoftwarePriceLabel,
  getKioskPnpBreakdown,
  buildTrialDuration,
} from 'utilities/software';

import { ReactComponent as SquareGreyLogo } from 'typescript/apps/checkout_v3/components/order_summary/components/square-grey.svg';
import styles from 'typescript/apps/checkout_v3/components/order_summary/components/cart_item.module.scss';

export type CartItemProps = {
  label: string;
  cost: string; // unit price * quantity
  currency: string;
  imgSrc?: string;
  quantity: number;
  serialNumber?: string;
  plan?: CartSummaryPlan;
  isSoftware?: boolean;
  isPnpSubscribed?: boolean;
  locationCount?: number;
  kioskDeviceCount?: number;
  baseProductKey?: string;
};

export const CartItem = ({
  label,
  cost,
  currency,
  quantity,
  imgSrc,
  plan,
  isSoftware,
  isPnpSubscribed = false,
  locationCount = 1,
  kioskDeviceCount = 1,
  baseProductKey,
}: CartItemProps) => {
  const { t } = useTranslation();

  const costStr = isSoftware
    ? getSoftwarePriceLabel({
        plan,
        t,
        isPnpSubscribed,
        locationCount,
        kioskDeviceCount,
        baseProductKey,
      })
    : toCurrency({ amount: Number.parseFloat(cost), currency });
  // TODO: update to handle warranties when supported in new checkout flow
  // const subtext = serialNumber
  //   ? t('checkout.receipt.label.replacementForSN', { serialNumber })
  //   : undefined;
  const localizedQuantity = quantity.toLocaleString(getLocaleString());

  const breakdownLines =
    isSoftware && plan
      ? getKioskPnpBreakdown({ plan, t, isPnpSubscribed, baseProductKey })
      : null;

  const trialMessage =
    isSoftware && plan?.free_trial_duration
      ? buildTrialDuration(t, plan.free_trial_duration)
      : null;

  return (
    <MarketRow className={styles.cartItem}>
      <MarketAccessory size="image" slot="leading-accessory">
        <div className={styles.accessoryOuterWrap}>
          <div className={styles.accessoryWrap}>
            {imgSrc ? <img src={imgSrc} alt={label} /> : <SquareGreyLogo />}
            <div className={`${styles.quantityWrap} paragraph-10`}>
              <div>{localizedQuantity}</div>
            </div>
          </div>
        </div>
      </MarketAccessory>
      <label slot="label">
        <span className="semibold-20" title={label}>
          {label}
        </span>
        {breakdownLines && (
          <span className="paragraph-10">
            {trialMessage && (
              <span style={{ display: 'block' }}>{trialMessage}</span>
            )}
            {breakdownLines.map((line, i) => (
              <span key={i} style={{ display: 'block' }}>
                {line.receiptLabel}
              </span>
            ))}
          </span>
        )}
      </label>
      <label slot="side-label">
        <span className="paragraph-20">{costStr}</span>
      </label>
    </MarketRow>
  );
};
